import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Printer, RefreshCw } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Alert from '../../components/ui/Alert'
import { qrService, type QrGenerateResponse } from '../../services/dumperApi'

const KINDS: Array<{ value: 'machinery' | 'subcontracted'; label: string }> = [
  { value: 'subcontracted', label: 'Volqueta subcontratada' },
  { value: 'machinery', label: 'Maquinaria propia' },
]

const DumperQrGenerator: React.FC = () => {
  const navigate = useNavigate()
  const [kind, setKind] = useState<'machinery' | 'subcontracted'>('subcontracted')
  const [id, setId] = useState('')
  const [result, setResult] = useState<QrGenerateResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleGenerate = async () => {
    setError(null)
    setSubmitting(true)
    try {
      const res = await qrService.generate({ kind, id: Number(id) })
      setResult(res)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo generar el QR')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRegenerate = async () => {
    if (!result) return
    setError(null)
    setSubmitting(true)
    try {
      const res = await qrService.regenerate(result.code)
      setResult(res)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo regenerar el QR')
    } finally {
      setSubmitting(false)
    }
  }

  const handlePrint = () => {
    if (!result) return
    const w = window.open('', '_blank')
    if (!w) return
    const imgSrc = qrService.imageUrl(result.code, 'png')
    w.document.write(`
      <html>
        <head><title>QR ${result.code}</title>
        <style>
          body { font-family: sans-serif; text-align: center; padding: 24px; }
          img { width: 320px; height: 320px; }
          h1 { font-size: 18px; margin: 8px 0; }
          p { margin: 4px 0; color: #555; font-size: 12px; }
        </style>
        </head>
        <body>
          <h1>Volqueta · ${result.kind}</h1>
          <p>Asset interno #${result.machineId}</p>
          <img src="${imgSrc}" alt="QR ${result.code}" onload="window.print()" />
          <p>Código: ${result.code}</p>
        </body>
      </html>
    `)
    w.document.close()
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-retri-blue px-3 py-3 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-white font-semibold">Generar QR volqueta</h1>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="text-sm text-gray-600">
              Genera un QR físico para pegar en la volqueta. El residente lo escanea para registrar
              viajes en RESIDENT_QR.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <div className="flex gap-2">
                {KINDS.map((k) => (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => setKind(k.value)}
                    className={`flex-1 px-3 py-2 rounded border text-sm ${
                      kind === k.value
                        ? 'bg-retri-blue text-white border-retri-blue'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label={kind === 'subcontracted' ? 'subcontractedMachineryId' : 'machineryId'}
              type="number"
              value={id}
              onChange={(e) => setId(e.target.value)}
              required
            />

            <Button
              onClick={handleGenerate}
              disabled={submitting || !id}
              className="w-full h-11"
            >
              {submitting ? 'Generando…' : 'Generar QR'}
            </Button>
          </CardContent>
        </Card>

        {result && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">QR generado</h3>
              <p className="text-xs text-gray-500">Código: {result.code}</p>
              <div className="flex justify-center bg-white border rounded p-4">
                <img
                  src={qrService.imageUrl(result.code, 'png')}
                  alt={`QR ${result.code}`}
                  className="w-64 h-64"
                />
              </div>
              <p className="text-xs text-gray-500 text-center break-all">
                URL: {result.url}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button onClick={handlePrint} className="h-11">
                  <Printer className="w-4 h-4 mr-2" /> Imprimir
                </Button>
                <a
                  href={qrService.imageUrl(result.code, 'png')}
                  download={`qr-${result.code}.png`}
                  className="inline-flex items-center justify-center h-11 px-4 rounded bg-retri-orange text-white hover:opacity-90 font-semibold text-sm"
                >
                  <Download className="w-4 h-4 mr-2" /> Descargar PNG
                </a>
                <Button variant="secondary" onClick={handleRegenerate} disabled={submitting}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Regenerar
                </Button>
              </div>
              <p className="text-xs text-retri-red">
                Regenerar invalida el QR físico anterior. Solo úsalo si se perdió o dañó el sticker.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  )
}

export default DumperQrGenerator
